/**
 * Utilitários centralizados para upload de arquivos
 *
 * Este módulo consolida as funções comuns de validação, formatação
 * e manipulação de arquivos usadas em toda a aplicação.
 */

// =============================================================================
// TIPOS
// =============================================================================

export interface FileValidationOptions {
  maxSizeMB?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
}

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

export type StorageBucket =
  | 'avatars'
  | 'investment-documents'
  | 'announcement-attachments'
  | 'documents'
  | 'diligencia-documents'
  | 'investor-kyc-documents';

// =============================================================================
// CONSTANTES
// =============================================================================

/** Limite padrão de tamanho de arquivo em MB */
export const DEFAULT_MAX_SIZE_MB = 10;

/** Tipos MIME permitidos por categoria */
export const ALLOWED_MIME_TYPES = {
  pdf: ['application/pdf'],
  images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  documents: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
    'application/msword', // doc
    'application/vnd.ms-excel', // xls
    'text/plain'
  ],
  all: [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.ms-excel',
    'text/plain'
  ]
} as const;

/** Extensões de arquivo por categoria */
export const ALLOWED_EXTENSIONS = {
  pdf: ['.pdf'],
  images: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  documents: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'],
  all: ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.doc', '.docx', '.xls', '.xlsx', '.txt']
} as const;

// =============================================================================
// FUNÇÕES DE VALIDAÇÃO
// =============================================================================

/**
 * Valida um arquivo com base nas opções fornecidas
 * @param file Arquivo a ser validado
 * @param options Opções de validação
 * @returns Resultado da validação
 */
export function validateFile(
  file: File,
  options: FileValidationOptions = {}
): FileValidationResult {
  const {
    maxSizeMB = DEFAULT_MAX_SIZE_MB,
    allowedTypes,
    allowedExtensions
  } = options;

  // Validar tamanho
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    return {
      valid: false,
      error: `Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`
    };
  }

  // Validar tipo MIME
  if (allowedTypes && allowedTypes.length > 0) {
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Tipo de arquivo não permitido. Permitidos: ${formatAllowedTypes(allowedTypes)}`
      };
    }
  }

  // Validar extensão
  if (allowedExtensions && allowedExtensions.length > 0) {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const normalizedExtensions = allowedExtensions.map(ext => ext.toLowerCase());

    if (!normalizedExtensions.includes(fileExtension)) {
      return {
        valid: false,
        error: `Extensão não permitida. Permitidas: ${allowedExtensions.join(', ')}`
      };
    }
  }

  return { valid: true };
}

/**
 * Valida múltiplos arquivos
 */
export function validateFiles(
  files: File[],
  options: FileValidationOptions = {}
): { valid: File[]; invalid: Array<{ file: File; error: string }> } {
  const valid: File[] = [];
  const invalid: Array<{ file: File; error: string }> = [];

  for (const file of files) {
    const result = validateFile(file, options);
    if (result.valid) {
      valid.push(file);
    } else {
      invalid.push({ file, error: result.error || 'Erro desconhecido' });
    }
  }

  return { valid, invalid };
}

// =============================================================================
// FUNÇÕES DE FORMATAÇÃO
// =============================================================================

/**
 * Formata tamanho de arquivo para exibição
 * @param bytes Tamanho em bytes
 * @returns String formatada (ex: "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Formata lista de tipos permitidos para exibição
 */
export function formatAllowedTypes(types: string[]): string {
  const extensionMap: Record<string, string> = {
    'application/pdf': 'PDF',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/webp': 'WebP',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/msword': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'text/plain': 'Texto'
  };

  return types.map(type => extensionMap[type] || type).join(', ');
}

/**
 * Retorna ícone emoji baseado no tipo MIME
 */
export function getFileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word')) return '📝';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
  if (mimeType === 'text/plain') return '📃';
  return '📎';
}

/**
 * Retorna cor baseada no tipo de arquivo
 */
export function getFileColor(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'text-purple-600';
  if (mimeType === 'application/pdf') return 'text-red-600';
  if (mimeType.includes('word')) return 'text-blue-600';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'text-green-600';
  return 'text-gray-600';
}

// =============================================================================
// FUNÇÕES DE SANITIZAÇÃO
// =============================================================================

/**
 * Sanitiza nome de arquivo para uso seguro em URLs e storage
 * @param fileName Nome original do arquivo
 * @returns Nome sanitizado
 */
export function sanitizeFileName(fileName: string): string {
  // Remove caracteres especiais, mantendo apenas letras, números, pontos, hífens e underscores
  return fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
}

/**
 * Gera path único para storage
 * @param prefix Prefixo do path (ex: ID do investimento)
 * @param fileName Nome do arquivo
 * @returns Path único com timestamp
 */
export function generateStoragePath(prefix: string, fileName: string): string {
  const timestamp = Date.now();
  const sanitized = sanitizeFileName(fileName);
  return `${prefix}/${timestamp}_${sanitized}`;
}

/**
 * Gera path compartilhado para documentos visíveis por múltiplos usuários
 */
export function generateSharedStoragePath(prefix: string, fileName: string): string {
  const timestamp = Date.now();
  const sanitized = sanitizeFileName(fileName);
  return `shared/${prefix}/${timestamp}_${sanitized}`;
}

// =============================================================================
// FUNÇÕES AUXILIARES
// =============================================================================

/**
 * Verifica se arquivo é uma imagem
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Verifica se arquivo é um PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

/**
 * Obtém extensão do arquivo
 */
export function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

/**
 * Cria objeto URL para preview de arquivo
 * Lembre-se de revogar com URL.revokeObjectURL quando não precisar mais
 */
export function createFilePreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Converte bytes para MB
 */
export function bytesToMB(bytes: number): number {
  return bytes / (1024 * 1024);
}

/**
 * Converte MB para bytes
 */
export function mbToBytes(mb: number): number {
  return mb * 1024 * 1024;
}
